import type { PriceId } from '@iheartjane/types';
import get from 'lodash-es/get';

import type { SpecialRulesV2 } from '@jane/business-admin/types';
import { buildBundleConditions, buildRules } from '@jane/business-admin/util';
// eslint-disable-next-line no-restricted-imports
import type {
  AdditionalProductWeight,
  BulkPricingConditions,
  BundleProductConditions,
} from '@jane/shared/models';

import { getApplyToSelection } from '../../form';

export const convertBulkConditionsToRules = (
  bulkConditions?: BulkPricingConditions
) => {
  if (!bulkConditions) {
    return;
  }

  const rules = buildRules({
    excludeProduct: bulkConditions.excluded_product_ids,
    includeBrand: bulkConditions.brands,
    includeBrandId: bulkConditions.brand_ids,
    includeBulkWeights: bulkConditions.bulk_weights,
    includeCategory: bulkConditions.kinds,
    includeLineage: bulkConditions.lineage ? [bulkConditions.lineage] : false,
    includeProduct: bulkConditions.included_product_ids,
  });

  return {
    apply_to: getApplyToSelection(rules),
    rules,
  };
};

export const convertRulesToBulkConditions = ({
  rules,
  max_applications_per_cart,
  target_weight,
}: {
  max_applications_per_cart: number;
  rules: SpecialRulesV2;
  target_weight: PriceId | AdditionalProductWeight;
}) => {
  return {
    excluded_product_ids: get(rules, 'excludes.0.product_ids'),
    brands: get(rules, 'includes.0.brands'),
    brand_ids: get(rules, 'includes.0.brand_ids'),
    bulk_weights: get(rules, 'includes.0.bulk_weights'),
    included_product_ids: get(rules, 'includes.0.product_ids'),
    kinds: get(rules, 'includes.0.kinds'),
    max_applications_per_cart,
    lineage: get(rules, 'includes.0.lineages', [])[0],
    target_weight,
  };
};

// Bundle conditions historically stored a single lineage at `lineage` (string).
// Multi-lineage selection persists at `lineages` (string[]). Prefer the plural
// shape; fall back to wrapping the singular for legacy data.
const lineageRuleInput = (side?: {
  lineage?: string;
  lineages?: string[];
}): string[] | false => {
  if (side?.lineages && side.lineages.length > 0) return side.lineages;
  if (side?.lineage) return [side.lineage];
  return false;
};

export const convertBundleConditionsToRules = (
  weightValuesEnabled: boolean,
  bundleConditions?: BundleProductConditions
) => {
  if (!bundleConditions) {
    return;
  }

  const requiredRules = buildRules({
    excludeProduct: bundleConditions.independent?.excluded_product_ids,
    includeBrand: bundleConditions.independent?.brands,
    includeBrandId: bundleConditions.independent?.brand_ids,
    includeCategory: bundleConditions.independent?.kinds,
    includeLineage: lineageRuleInput(bundleConditions.independent),
    includeProduct: bundleConditions.independent?.included_product_ids,
    includePrices: bundleConditions.independent?.product_threshold,
    includeWeight:
      !weightValuesEnabled && bundleConditions.independent?.weights,
    includeWeightValue:
      weightValuesEnabled && bundleConditions.independent?.weight_values,
  });

  const discountedRules = buildRules({
    excludeProduct: bundleConditions.dependent?.excluded_product_ids,
    includeBrand: bundleConditions.dependent?.brands,
    includeBrandId: bundleConditions.dependent?.brand_ids,
    includeCategory: bundleConditions.dependent?.kinds,
    includeLineage: lineageRuleInput(bundleConditions.dependent),
    includeProduct: bundleConditions.dependent?.included_product_ids,
    includePrices: bundleConditions.dependent?.product_threshold,
    includeWeight: !weightValuesEnabled && bundleConditions.dependent?.weights,
    includeWeightValue:
      weightValuesEnabled && bundleConditions.dependent?.weight_values,
  });

  return {
    required_rules: {
      apply_to: getApplyToSelection(requiredRules),
      rules: requiredRules,
    },
    discounted_rules: {
      apply_to: getApplyToSelection(discountedRules),
      rules: discountedRules,
    },
  };
};

export const convertRulesToBundleConditions = ({
  discountedRules,
  max_number_of_discounted_products,
  requiredRules,
  threshold_number_of_items_in_cart,
  settings,
  max_applications_per_cart,
}: {
  discountedRules: SpecialRulesV2;
  max_applications_per_cart: number;
  max_number_of_discounted_products: number;
  requiredRules: SpecialRulesV2;
  settings: any;
  threshold_number_of_items_in_cart: number;
}) => {
  const dependentConditions = {
    brandConditions: get(discountedRules, 'includes.0.brands'),
    brandIdConditions: get(discountedRules, 'includes.0.brand_ids'),
    includedProductConditions: get(discountedRules, 'includes.0.product_ids'),
    excludedProductConditions: get(discountedRules, 'excludes.0.product_ids'),
    kindConditions: get(discountedRules, 'includes.0.kinds'),
    lineageConditions: get(discountedRules, 'includes.0.lineages'),
    priceConditions: get(discountedRules, 'product_threshold'),
    weightConditions: get(discountedRules, 'includes.0.weights'),
    weightValueConditions: get(discountedRules, 'includes.0.weight_values'),
  };
  const independentConditions = {
    brandConditions: get(requiredRules, 'includes.0.brands'),
    brandIdConditions: get(requiredRules, 'includes.0.brand_ids'),
    includedProductConditions: get(requiredRules, 'includes.0.product_ids'),
    excludedProductConditions: get(requiredRules, 'excludes.0.product_ids'),
    kindConditions: get(requiredRules, 'includes.0.kinds'),
    lineageConditions: get(requiredRules, 'includes.0.lineages'),
    priceConditions: get(requiredRules, 'product_threshold'),
    weightConditions: get(requiredRules, 'includes.0.weights'),
    weightValueConditions: get(requiredRules, 'includes.0.weight_values'),
  };

  return {
    dependent: {
      ...buildBundleConditions(dependentConditions),
      max_number_of_discounted_products,
    },
    independent: {
      ...buildBundleConditions(independentConditions),
      threshold_number_of_items_in_cart,
    },
    settings,
    max_applications_per_cart,
  };
};
